import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Plus, RotateCcw, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DataTable } from '../components/ui/data-table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useApiConfig } from '../hooks/useApiConfig';
import { BACKEND_BASE_URL } from '../config/backendConfig';
import {
  fetchInternalUsersList, createInternalUserApi, resetInternalUserPasswordApi, updateInternalUserRoleApi,
} from '../utils/api';

const ROLES = ['KAM', 'ACCOUNT_EXECUTIVE', 'EXECUTIVE', 'ADMIN'];

function Field({ label, children }) {
  return <div className="ui-field"><label className="form-label">{label}</label>{children}</div>;
}

function CreateInternalUserDialog({ onCreate, onDone }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [role, setRole] = useState('KAM');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        contactNumber: contactNumber.trim() || undefined,
        role,
        password,
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add internal user</DialogTitle>
        <DialogDescription>Creates a NAKAD-staff login (internal_users) with the role you pick below.</DialogDescription>
      </DialogHeader>
      <form className="ui-form-grid" onSubmit={handleSubmit}>
        <div className="ui-form-row">
          <Field label="First name">
            <Input className="form-input" value={firstName} onChange={e => setFirstName(e.target.value)} required autoFocus />
          </Field>
          <Field label="Last name" hint="Optional">
            <Input className="form-input" value={lastName} onChange={e => setLastName(e.target.value)} />
          </Field>
        </div>
        <Field label="Email">
          <Input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </Field>
        <div className="ui-form-row">
          <Field label="Role">
            <select className="form-input" value={role} onChange={e => setRole(e.target.value)}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Contact number" hint="Optional">
            <Input className="form-input" value={contactNumber} onChange={e => setContactNumber(e.target.value)} />
          </Field>
        </div>
        <Field label="Initial password" hint="At least 8 characters">
          <Input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} minLength={8} required />
        </Field>

        {error && <p className="ui-form-error">{error}</p>}
        <DialogFooter>
          <Button type="submit" disabled={submitting}>{submitting ? 'Creating…' : 'Create user'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ResetPasswordDialog({ user, onReset, onDone }) {
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onReset(user.id, newPassword);
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Reset password</DialogTitle>
        <DialogDescription>Sets a new password for {user.firstName} {user.lastName} ({user.email}) directly — no current password required.</DialogDescription>
      </DialogHeader>
      <form className="ui-form-grid" onSubmit={handleSubmit}>
        <Field label="New password" hint="At least 8 characters">
          <Input className="form-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} minLength={8} required autoFocus />
        </Field>
        {error && <p className="ui-form-error">{error}</p>}
        <DialogFooter>
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Set password'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function UpdateRoleDialog({ user, onUpdate, onDone }) {
  const [role, setRole] = useState(user.role);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onUpdate(user.id, role);
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Change internal user role</DialogTitle>
        <DialogDescription>
          Update the role for {user.firstName} {user.lastName || ''} ({user.email}).
          Their current session will require a fresh login after the change.
        </DialogDescription>
      </DialogHeader>
      <form className="ui-form-grid" onSubmit={handleSubmit}>
        <Field label="Role">
          <select className="form-input" value={role} onChange={e => setRole(e.target.value)} autoFocus>
            {ROLES.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </Field>
        {error && <p className="ui-form-error">{error}</p>}
        <DialogFooter>
          <Button type="submit" disabled={submitting || role === user.role}>
            {submitting ? 'Saving…' : 'Update role'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

export default function InternalUsersPage() {
  const { currentUser } = useAuth();
  const cfg = useApiConfig();

  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [changingRole, setChangingRole] = useState(null);

  const load = useCallback(async () => {
    if (!cfg) return;
    setError(null);
    try {
      setUsers(await fetchInternalUsersList(BACKEND_BASE_URL, cfg));
    } catch (e) {
      setError(e.message);
    }
  }, [cfg]);

  useEffect(() => { load(); }, [load]);

  const rows = users || [];

  const userColumns = useMemo(() => [
    {
      id: 'name', accessorFn: user => `${user.firstName} ${user.lastName || ''}`.trim(),
      header: 'Name', filterFn: 'includesString', meta: { label: 'Name', filterVariant: 'text' },
      cell: ({ row }) => <span className="user-name">{row.original.firstName} {row.original.lastName}</span>,
    },
    {
      accessorKey: 'email', header: 'Email', filterFn: 'includesString',
      meta: { label: 'Email', filterVariant: 'text' },
      cell: ({ row }) => <span className="email-cell">{row.original.email}</span>,
    },
    {
      accessorKey: 'role', header: 'Role', filterFn: 'equalsString',
      meta: { label: 'Role', filterVariant: 'select', filterOptions: ROLES },
      cell: ({ row }) => <span className="desig-chip">{row.original.role}</span>,
    },
    {
      accessorKey: 'contactNumber', header: 'Contact', filterFn: 'includesString',
      meta: { label: 'Contact', filterVariant: 'text' },
      cell: ({ row }) => row.original.contactNumber
        ? <span className="contact-ok">{row.original.contactNumber}</span>
        : <span className="contact-missing">—</span>,
    },
    {
      accessorKey: 'status', header: 'Status', filterFn: 'equalsString',
      meta: { label: 'Status', filterVariant: 'select' },
      cell: ({ row }) => (
        <span className={`pill ${row.original.status === 'ACTIVE' ? 'pill-green' : 'pill-grey'}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'actions', header: '', enableSorting: false, enableColumnFilter: false, enableHiding: false,
      cell: ({ row }) => (
        <div className="internal-user-actions">
          <Button variant="ghost" size="sm" onClick={() => setChangingRole(row.original)}>Change role</Button>
          <Button variant="ghost" size="sm" onClick={() => setResetting(row.original)}>
            <KeyRound size={14} />Reset password
          </Button>
        </div>
      ),
    },
  ], []);

  if (currentUser?.role !== 'ADMIN') {
    return <div className="page-content"><div className="empty-state">Internal user management is restricted to admins.</div></div>;
  }
  if (!cfg) return null;

  return (
    <div className="page-content">
      <div className="section-header">
        <Users size={18} />
        <h2>Internal Users</h2>
        <span className="row-count">{users ? users.length : '…'}</span>
        <div className="header-right">
          <Button variant="outline" size="sm" onClick={load}><RotateCcw size={15} />Refresh</Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button size="sm"><Plus size={15} />Add internal user</Button></DialogTrigger>
            {addOpen && (
              <CreateInternalUserDialog
                onCreate={payload => createInternalUserApi(BACKEND_BASE_URL, cfg, payload)}
                onDone={() => { setAddOpen(false); load(); }}
              />
            )}
          </Dialog>
        </div>
      </div>

      {error && <p className="error-msg">{error}</p>}
      {!users && !error && <div className="loading-state"><div className="spinner" /><p>Loading internal users…</p></div>}

      {users && <DataTable columns={userColumns} data={rows} emptyMessage="No internal users match the selected filters." />}

      {resetting && (
        <Dialog open onOpenChange={open => !open && setResetting(null)}>
          <ResetPasswordDialog
            user={resetting}
            onReset={(id, newPassword) => resetInternalUserPasswordApi(BACKEND_BASE_URL, cfg, id, newPassword)}
            onDone={() => setResetting(null)}
          />
        </Dialog>
      )}

      {changingRole && (
        <Dialog open onOpenChange={open => !open && setChangingRole(null)}>
          <UpdateRoleDialog
            user={changingRole}
            onUpdate={(id, role) => updateInternalUserRoleApi(BACKEND_BASE_URL, cfg, id, role)}
            onDone={() => { setChangingRole(null); load(); }}
          />
        </Dialog>
      )}
    </div>
  );
}
